<template>
  <div>
    <div v-if="visibleMemos && visibleMemos.length > 0">
      <template v-for="(memo, index) in visibleMemos" :key="index">
        <div class="pb-4">
          <div class="border-t border-dashed relative pt-4 pb-4">
            <div class="absolute w-full text-center -top-4 z-10">
              <div class="inline text-center border border-dashed rounded p-1 text-xs text-nowrap"
                   :class="memo.secret ? makeBgColorClasses('yellow') : 'bg-white'">
                {{ dayjs(memo.updatedTime).format('YYYY-MM-DD HH:mm') }} / {{ memo.userName ?? memo.userid }}
                <template v-if="memo.secret">
                  (나만 보임)
                </template>
                <!-- 260922 개별 주소 전용 메모 표시. 이 메모는 지금 이 주소에서만 보인다 -->
                <template v-if="memo.exactPageUrl">
                  (이 주소 전용)
                </template>
              </div>
            </div>
            <div>
              <div class="break-words" v-html="nl2br(memo.helpContents)"></div>
            </div>
            <div class="flex justify-end gap-1">
              <v-btn icon="far fa-pen-to-square" size="x-small" @click="startEdit(memo)"></v-btn>
              <v-btn icon="far fa-trash" size="x-small" :loading="isRemoving" @click="remove(memo.no)"></v-btn>
            </div>
          </div>
        </div>
      </template>
    </div>
    <MiscDYNoContents v-else class="mb-8"/>

    <div v-if="!isFormVisible" class="text-center">
      <v-btn append-icon="far fa-message-plus" @click="isFormVisible = true; selectedMemoNo = undefined" color="primary">
        등록하기
      </v-btn>
    </div>

    <div :class="isFormVisible ? 'block' : 'hidden'">
      <DYSummerNote @init="summerNoteInit" db-domain="doyacinc" table-name="page_help_memo" field-name="fileName"
                    @upload:file="summernoteUpload" :subject-no="selectedMemoNo ? selectedMemoNo : undefined">
      </DYSummerNote>

      <!-- 260922 스위치가 3개로 늘어 한 줄에 다 들어가지 않으므로 flex-wrap 허용 -->
      <div class="flex justify-between items-center gap-2 p-4 flex-wrap">
        <v-switch v-model="params.secret" label="나만 보기" density="comfortable" color="primary" hide-details />
        <v-switch :model-value="params.targetSite === 'doyacinc'" label="모든 사이트에 등록" density="comfortable" hide-details
                  color="primary" @update:model-value="params.targetSite = $event ? 'doyacinc' : undefined"/>
        <!-- 260922 개별 주소 전용 메모.
             기본(꺼짐)은 기존과 동일하게 페이지 단위(pageUrl)라 그 화면의 모든 상세(typedPk)에서 보이고,
             켜면 지금 보고 있는 주소(currentPath)에서만 보인다. 쿼리스트링(?page=2 등)은 판정에서 제외한다. -->
        <v-tooltip :text="`이 메모를 ${currentPath} 에서만 보이게 합니다 (물음표 뒤 쿼리는 무시)`" location="top">
          <template #activator="{ props: tooltipProps }">
            <div v-bind="tooltipProps">
              <v-switch v-model="isExactUrlOnly" label="이 주소에서만" density="comfortable" color="primary" hide-details />
            </div>
          </template>
        </v-tooltip>
        <div class="flex gap-1">
          <v-btn append-icon="far fa-ban" @click="clearForm">
            닫기
          </v-btn>
          <v-btn append-icon="far fa-send" :loading="isSending" @click="send" color="primary">
            {{ params.no ? '수정' : '등록' }}
          </v-btn>
        </div>
        <!-- 스위치를 켠 상태에서는 어떤 주소에 묶이는지 눈으로 확인할 수 있게 경로를 그대로 보여준다 -->
        <div v-if="isExactUrlOnly" class="w-full text-xs text-gray-500 break-all">
          이 메모는 <span class="font-bold">{{ currentPath }}</span> 에서만 보입니다.
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {library} from '@fortawesome/fontawesome-svg-core';
import {faBan, faMessagePlus, faPenToSquare, faSend, faTrash} from '@fortawesome/pro-regular-svg-icons';
import {
  useDYAPageHelpMemoApi,
  type DYAPageHelpMemo,
  type DYAPageHelpMemoFormParams,
  type DYAPageHelpMemoGetListParams
} from '../composables/dya-page-help-memo';
import {notify} from '@kyvg/vue3-notification';
import {useDYUserStore} from '~/modules/nuxt3-module-doyac/stores/user';
import {useDYAdminMainStore} from '../stores/main';
import {cloneDeep} from 'lodash-es';
import {formatDate, nl2br} from '~/modules/nuxt3-module-doyac/libs/utils';
import dayjs from 'dayjs';
import type {SendUploadFiles} from '~/modules/nuxt3-module-doyac/composables/dy-file-uploader';
import {useDYAppConfig} from '~/modules/nuxt3-module-doyac/composables/config';
import {useDYTailwindUtil} from "~/modules/nuxt3-module-doyac/composables/dy-tailwind-util";
import { useDYAuth } from '~/modules/nuxt3-module-doyac/composables/auth';

library.add(faBan, faMessagePlus, faPenToSquare, faSend, faTrash)

const props = withDefaults(defineProps<{
  type: 'main' | 'section'
}>(), {})

const {dbDomain} = useDYAppConfig()
const adminMainStore = useDYAdminMainStore()
const userStore = useDYUserStore()
const {getList, insert, update, remove: _remove} = useDYAPageHelpMemoApi()
const {makeBgColorClasses} = useDYTailwindUtil()
const memos = ref<DYAPageHelpMemo[]>([])
const { isAdmin } = useDYAuth()

const childSummernote = ref()
const params = ref<DYAPageHelpMemoFormParams>({
  helpContents: '',
  secret: false,
})
const isFormVisible = ref(false)
const isSending = ref(false)
const isRemoving = ref(false)

const selectedMemoNo = ref<number>()

// ─────────────────────────────────────────────────────────────────────────────
// 260922 개별 주소 전용 메모
//   pageUrl 은 DYAPageHeader 에 개발자가 박아둔 고정 문자열(예 '/tourguide/route/list')이라
//   상세페이지는 typedPk 가 달라도 모두 같은 pageUrl 을 쓴다 = 메모가 전 상세에 다 보인다.
//   그 기본 동작은 그대로 두고, 등록 시 '이 주소에서만' 을 켠 메모만 exactPageUrl 에 실제 경로를 저장해
//   그 경로에서만 보이게 한다. 판정은 경로(route.path)만 비교하고 쿼리스트링은 무시한다
//   (?page=2, ?routeNo=5 같은 값 때문에 메모가 사라지지 않도록. 2026-09-22 사용자 결정).
// ─────────────────────────────────────────────────────────────────────────────
const route = useRoute()
/** 지금 보고 있는 주소의 경로(쿼리스트링 제외) */
const currentPath = computed(() => route.path)
/** 등록/수정 폼의 '이 주소에서만' 스위치 상태 */
const isExactUrlOnly = ref(false)

const computedParams = computed(() => {
  const temp: DYAPageHelpMemoFormParams = {
    ...params.value
  }
  if (props.type === 'main') {
    temp.pageUrl = adminMainStore.pageHelpMemoNavSettings.pageUrl
    temp.componentId = undefined
    temp.sectionId = undefined
  } else {
    temp.pageUrl = undefined
    temp.componentId = adminMainStore.pageHelpMemoNavSettings.componentId
    temp.sectionId = adminMainStore.pageHelpMemoNavSettings.sectionId
  }
  // 해제 시에는 반드시 null (undefined 면 JSON 에서 키가 빠져 서버가 기존 값을 유지한다)
  temp.exactPageUrl = isExactUrlOnly.value ? currentPath.value : null
  return temp
})

const visibleMemos = computed(() => {
  return memos.value.filter(e => {
    // 나만 보기
    if (e.secret && e.userid !== userStore.id) return false
    // 260922 개별 주소 전용 메모는 그 경로에서만 노출. 값이 없으면 기존처럼 페이지 단위로 노출
    if (e.exactPageUrl && e.exactPageUrl !== currentPath.value) return false
    return true
  })
})

const summerNoteInit = (summerNote: any) => {
  childSummernote.value = summerNote
}

const summernoteUpload = (param: SendUploadFiles) => {
  params.value.contentImageFileData = param.fileFormData

}

const send = async () => {
  isSending.value = true
  params.value.helpContents = childSummernote.value.getContents()

  if (!params.value.helpContents) {
    notify({type: 'error', text: '본문 내용은 필수입니다'})
    isSending.value = false
    return
  }

  // pageUrl이 없으면 API에 빈 값이 전달되어 정상 노출 불가
  if (props.type === 'main' && !computedParams.value.pageUrl) {
    notify({type: 'error', text: 'pageUrl이 설정되지 않았습니다. 정상적인 노출이 되지 않으니 개발자에게 전달해주세요.'})
    isSending.value = false
    return
  }

  // fetch로 보낼 데이터는 반응형이면 데이터 변경이 있을 때마다 중복 페칭 이슈가 발생하므로 주의
  if (computedParams.value.no) {
    const newMemo = await update(cloneDeep(computedParams.value))
    const index = memos.value.findIndex(e => e.no === newMemo.no)
    if (index >= 0) {
      memos.value[index] = newMemo
    }
  } else {
    const newMemo = await insert(cloneDeep(computedParams.value))
    memos.value.push(newMemo)
  }

  isSending.value = false
  clearForm()
}

const remove = async (no: number) => {
  if (confirm('정말로 삭제합니까?')) {
    isRemoving.value = true
    try {
      await _remove(no)
    } catch (e: any) {

    }
    isRemoving.value = false
    // 260922 기존엔 화면 목록(visibleMemos)의 index 로 원본 memos 를 splice 해서,
    //   '나만 보기'·'이 주소 전용'으로 걸러진 메모가 있으면 엉뚱한 메모가 화면에서 사라졌다. 주키로 찾아 제거한다.
    const index = memos.value.findIndex(e => e.no === no)
    if (index >= 0) memos.value.splice(index, 1)
  }
}

const startEdit = (memo: DYAPageHelpMemo) => {
  params.value = {...memo}
  // 260922 수정 시작 시 '이 주소에서만' 스위치 상태 복원(끄고 저장하면 전체 노출로 되돌아간다)
  isExactUrlOnly.value = !!memo.exactPageUrl
  /**
   * 현재 선택된 수정메모의 주키
   */
  selectedMemoNo.value = params.value.no
  childSummernote.value.setContents(nl2br(params.value.helpContents))
  isFormVisible.value = true
}

const clearForm = () => {
  childSummernote.value.setContents('')

  params.value.no = undefined
  params.value.helpContents = ''
  params.value.secret = false
  isExactUrlOnly.value = false // 260922 다음 등록이 의도치 않게 '이 주소 전용'으로 들어가지 않도록 초기화
  isFormVisible.value = false
}

const fetch = async () => {
  if(!isAdmin.value) return

  const fetchParams: DYAPageHelpMemoGetListParams = {
    targetSite: `{{(['${dbDomain as 'doyacweb' | 'doyacart' | 'myfirstguide'}','doyacinc'],i)}}`
  }
  if (props.type === 'main') {
    if (!adminMainStore.pageHelpMemoNavSettings.pageUrl) {
      memos.value = []
      return
    }
    fetchParams.pageUrl = adminMainStore.pageHelpMemoNavSettings.pageUrl
  } else {
    if (!adminMainStore.pageHelpMemoNavSettings.componentId && !adminMainStore.pageHelpMemoNavSettings.sectionId) {
      memos.value = []
      return
    }
    fetchParams.componentId = adminMainStore.pageHelpMemoNavSettings.componentId
    fetchParams.sectionId = adminMainStore.pageHelpMemoNavSettings.sectionId
  }

  const result = await getList(fetchParams)
  memos.value = result.items
}

watch(() => adminMainStore.pageHelpMemoNavSettings.pageUrl, fetch)
watch(() => adminMainStore.pageHelpMemoNavSettings.componentId, fetch)
watch(() => adminMainStore.pageHelpMemoNavSettings.sectionId, fetch)

// 드로어가 열리는 시점에 pageUrl이 없으면 개발자에게 경고
// nextTick으로 지연하여 페이지 전환 시 반응형 캐스케이딩이 정착된 후 체크 (타이밍 이슈 방지)
if (props.type === 'main') {
  watch(() => adminMainStore.pageHelpMemoNavSettings.isMainOpened, async (opened) => {
    if (opened) {
      await nextTick()
      if (!adminMainStore.pageHelpMemoNavSettings.pageUrl) {
        notify({type: 'warn', text: '이 페이지에 pageUrl이 설정되지 않았습니다. 개발자에게 전달해주세요.'})
      }
    }
  })
}

if (props.type === 'main') {
  watch(() => visibleMemos.value.length, (value) => {
    adminMainStore.pageHelpMemoNavSettings.hasPageMemos = value > 0
  })
}

onMounted(fetch)
</script>